import type { CreativeFamily } from './creative-api';

/** How a banner family read came out: an answer (possibly no family), or a read that failed. */
export type BannerFamilyLoad = { ok: true; family: CreativeFamily | undefined } | { ok: false };

/**
 * Load the banner family for the Creatives page.
 *
 * The spark-creatives service is the ONLY source of banner content. When it is not configured there is nothing
 * to read, so this answers no family (the page's empty state). When its read fails or times out this answers
 * `ok: false`, so the page shows an error with a retry: a failed read is not a brand with no banners. We
 * deliberately do NOT fall back to the brand's logo family: a banner-service outage must never masquerade as
 * banner content, which used to render the brand's logo slots (Top nav, Footer, ...) as if they were the
 * site's banners.
 *
 * Never rejects (an abort surfaces as a failed read); the caller's abort guard discards it when the effect was
 * torn down.
 */
export async function loadBannerFamily(deps: {
  serviceConfigured: boolean;
  fetchFromService: () => Promise<CreativeFamily | undefined>;
}): Promise<BannerFamilyLoad> {
  if (!deps.serviceConfigured) return { ok: true, family: undefined };
  try {
    return { ok: true, family: await deps.fetchFromService() };
  } catch {
    return { ok: false };
  }
}
